import './CategoryPathing.scss';
import { useEffect, useMemo, useState } from 'react';
import { DatePicker } from '@Components/DatePicker';
import { Design } from '@Components/designConstants';
import { FullPageSpinner, Spinner } from '@Components/Spinner/Spinner';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { CategoryJourneyMatrix } from './components/CategoryJourneyMatrix';
import { KpiBar } from './components/KpiBar';
import { ProductDeepDive } from './components/ProductDeepDive';
import { useCategoryPathingData, useCategoryPathingDemoData } from './utils/useCategoryPathingData';
import { CategoryPathingData } from './category-pathing-types';

interface CategoryPathingContentProps {
  data: CategoryPathingData;
  onPeriodChange: (reportEndDate: string) => void;
  isBusy: boolean;
}

const CategoryPathingContent = ({ data, onPeriodChange, isBusy }: CategoryPathingContentProps) => {
  // `reportStartDate` is already a month-start date — exactly the value DatePicker's
  // `fullMonthsOnly` mode selects/compares against. Only months with a matching available period
  // are selectable; picking one reports back its `reportEndDate` (what the data hook keys off).
  const reportEndDateByStartDate = useMemo(
    () =>
      new Map(data.availablePeriods.map(period => [period.reportStartDate, period.reportEndDate])),
    [data.availablePeriods]
  );
  const selectedReportStartDate = data.selectedPeriod?.reportStartDate;

  return (
    <div className='category-pathing'>
      <div className='amc-intro'>
        <div className='amc-intro-eyebrow'>Amazon Marketing Cloud</div>
        <div className='amc-intro-headline'>
          How new-to-brand customers move through the product portfolio
        </div>
        <div className='amc-intro-sub'>
          Pathing analysis of new-to-brand purchases and the next product each customer buys.
        </div>
        {data.availablePeriods.length > 0 && (
          <DatePicker
            isSingle
            fullMonthsOnly
            range={
              selectedReportStartDate
                ? { start: selectedReportStartDate, end: selectedReportStartDate }
                : undefined
            }
            onChange={newRange => {
              const reportEndDate = reportEndDateByStartDate.get(newRange.start);
              if (reportEndDate) {
                onPeriodChange(reportEndDate);
              }
            }}
            isOutsideRange={date => !reportEndDateByStartDate.has(date)}
            fiscalCalOverride={{}}
            design={Design.NEUTRAL}
            placeholder='Select Month'
          />
        )}
      </div>

      <KpiBar kpis={data.kpis} reportWindowLabel={data.reportWindowLabel} />

      {/* WidgetContainer has no isLoading prop (unlike ChartContainer) — swap in a spinner
          ourselves so a period change reads the same way other AMC pages' ChartContainers do. */}
      <WidgetContainer header='Category Journey' subHeader='Entry category → next category bought.'>
        {isBusy ? (
          <Spinner />
        ) : (
          <CategoryJourneyMatrix categories={data.categories} categoryRows={data.categoryRows} />
        )}
      </WidgetContainer>

      <WidgetContainer
        header='Product Deep Dive'
        subHeader='ASIN-level pathing for the categories with sufficient volume.'
      >
        {isBusy ? (
          <Spinner />
        ) : (
          <ProductDeepDive
            asinRows={data.asinRows}
            asinMap={data.asinMap}
            deepDiveCategories={data.deepDiveCategories}
          />
        )}
      </WidgetContainer>
    </div>
  );
};

interface CategoryPathingProps {
  amcInstanceId?: string;
  // Renders the static CDN-CSV demo dataset instead of the live endpoint (hidden comparison route).
  demo?: boolean;
}

export const CategoryPathing = ({ amcInstanceId, demo = false }: CategoryPathingProps) => {
  const { cid } = useCompanyInfo();
  const [reportEndDate, setReportEndDate] = useState<string | undefined>(undefined);
  // Both hooks are always called (Rules of Hooks); only the active source fetches.
  const liveQuery = useCategoryPathingData(
    demo ? undefined : amcInstanceId,
    demo ? undefined : cid,
    reportEndDate
  );
  const demoQuery = useCategoryPathingDemoData(demo);
  const { data, isLoading, isFetching, error } = demo ? demoQuery : liveQuery;
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load AMC data",
        message: `Failed to load AMC pathing data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // The very first fetch has no reportEndDate to send (state starts undefined), so the backend
  // resolves its own default period (`availablePeriods[0]`) rather than an explicit date match —
  // a different code path with no guarantee it returns the same rows an explicit request for that
  // same period would (confirmed live: the no-param and explicit-date requests for what looked
  // like the same month returned different data). Sync the state to the server's own choice as
  // soon as it's known, so every fetch from here on always sends an explicit reportEndDate.
  useEffect(() => {
    if (!reportEndDate && !demo && data?.selectedPeriod) {
      setReportEndDate(data.selectedPeriod.reportEndDate);
    }
  }, [reportEndDate, demo, data]);

  if (isLoading) return <FullPageSpinner />;

  // No data with nothing in flight means the client has no AMC instance (the live query is
  // disabled) or the load failed — show a message rather than a blank panel.
  if (!data) {
    return (
      <div className='category-pathing-empty'>
        Category Pathing isn’t available for this client.
      </div>
    );
  }

  return (
    <CategoryPathingContent data={data} onPeriodChange={setReportEndDate} isBusy={isFetching} />
  );
};
